import { Injectable, OnDestroy } from '@angular/core';
import { MessageService } from './message.service';
import { Observable } from 'rxjs';
import { PySyncSocket } from '../pySyncSocket.module';
import { NotifierService } from 'angular-notifier';
import { OnInit } from '@angular/core';
import { RealtimeEvent } from '../models/syncEvents/realtimeEvents/realtimeEvent';
import { PersistenceEvent } from '../models/syncEvents/persistenceEvents/persistenceEvent';
import { ChatEvent} from '../models/syncEvents/chatEvents/chatEvent';

/**
 * this service is responsible for the connection to the websocket server for synchronization
 * this service connects to the persistence room onInit and changes the room on settingsupdate event
 * This service uses the SynchronizationSocket to connect to the Synchonization Server
 */
@Injectable()
export class SynchronizationService implements OnInit, OnDestroy {

  private currentPersistenceProjectKey: string;
  private currentVideoProjectKey: string;
  private currentRealtimeProjectKey: string;
  private currentCopilotProjectKey: string;
  private currentCopilotMeetingId: string;
  private currentChatProjectKey: string;
  public userCount = 0;


  constructor(
    private socket: PySyncSocket,
    private messageService: MessageService,
    private notifier: NotifierService) {
    this.subscribeEvent<any>('usercount').subscribe(value => {
      this.userCount = value.usercount;
    });
  }

  ngOnInit(): void {
    this.initOnSocketIOError();
  }

  ngOnDestroy(): void {
    this.leavePersistenceRoom();
    this.leaveRealtimeRoom();
    this.leaveVideoRoom();
    this.leaveChatRoom();
  }

  initOnSocketIOError() {
    this.socket.on('connect_error', function (_) {
      if (this.notifier) {
        this.notifierService.notify('warning', 'No connection to sync-server.');
      }
    });
  }

  /**
   * subscribes to all received events from the syncserver with the specified eventName
   */
  subscribeEvent<T>(eventName: string): Observable<T> {
    return this.socket.fromEvent(eventName);
  }

  /**
   * sends an Event with the specified name and Payload to the syncserver
   */
  sendEvent(eventName: string, data?: any): void {
    this.socket.emit(eventName, JSON.stringify(data));
  }

  // Use this method with typed events over the one below
  /**
   * @param event
   * @param excludeSelf Per default the sender of a persistence event does not receive
   * the event himself. Set to 'false' to override this behavior.
   */
  sendPersistenceEventNew(event: PersistenceEvent, excludeSelf = true): void {
    if (this.persistenceProjectValid()) {
      this.messageService.sendTrace(`sending PersistenceEvent ${event.type}`);
      const realTimeData = {
        type: event.type,
        projectId: this.currentPersistenceProjectKey,
        data: event.getPayload()
      };
      if (!excludeSelf) {
        realTimeData['excludeSelf'] = false;
      }
      this.sendEvent('persistenceEvent', realTimeData);
    }
  }

  sendChatEvent(event: ChatEvent, excludeSelf = true): void {
    if(this.chatProjectValid()){
      this.messageService.sendTrace(`sending ChatEvent ${event.type}`);
      const realTimeData = {
        type: event.type,
        projectId: this.currentChatProjectKey,
        data: event.getPayload()
      };
      if (!excludeSelf){
        realTimeData['excludeSelf'] = false;
      }
      this.sendEvent('chatEvent', realTimeData);
    }
  }

  // Deprecated. use method for typed events above
  sendPersistenceEvent(eventName: string, data?: any): void {
    if (this.persistenceProjectValid()) {
      this.messageService.sendTrace(`sending PersistenceEvent ${eventName}`);
      const realTimeData = {
        type: eventName,
        projectId: this.currentPersistenceProjectKey,
        data: data === undefined ? null : data
      };
      this.sendEvent('persistenceEvent', realTimeData);
    }
  }

  // Use this method with typed events in the future
  // Work-in-progress
  sendRealTimeEventNew(event: RealtimeEvent, excludeSelf = true): void {
    if (this.realtimeProjectValid()) {
      this.messageService.sendTrace(`sending RealTimeEvent ${event.type}`);
      const realTimeData = {
        type: event.type,
        projectId: this.currentRealtimeProjectKey,
        data: event.getPayload()
      };
      if (!excludeSelf) {
        realTimeData['excludeSelf'] = false;
      }
      this.sendEvent('realtimeEvent', realTimeData);
    }
  }

  // Deprecated. use method for typed events above
  sendRealTimeEvent(eventName: string, data?: any): void {
    if (this.realtimeProjectValid()) {
      this.messageService.sendTrace(`sending RealTimeEvent ${eventName}`);
      const realTimeData = {
        type: eventName,
        projectId: this.currentRealtimeProjectKey,
        data: data === undefined ? null : data
      };
      this.sendEvent('realtimeEvent', realTimeData);
    }
  }

  /**
   * join the project persistence room with the specified project key
   * @param projectKey
   */
  joinPersistenceRoom(projectKey: string): void {
    this.leavePersistenceRoom();
    this.currentPersistenceProjectKey = projectKey;

    // check for correct project key to join a persistenceroom
    if (this.persistenceProjectValid()) {
      const data = { 'projectId': projectKey };
      this.sendEvent('joinPersistenceRoom', data);
    }
  }

  private leavePersistenceRoom(): void {
    if (this.persistenceProjectValid()) {
      const data = { 'projectId': this.currentPersistenceProjectKey };
      this.sendEvent('leavePersistenceRoom', data);
    }
    this.currentPersistenceProjectKey = undefined;
  }


  joinRealtimeRoom(projectKey: string): void {
    this.leaveRealtimeRoom();
    this.currentRealtimeProjectKey = projectKey;
    if (this.realtimeProjectValid()) {
      const data = { 'projectId': this.currentRealtimeProjectKey };
      this.sendEvent('joinRealtimeRoom', data);
    }
  }

  leaveRealtimeRoom(): void {
    if (this.realtimeProjectValid()) {
      const data = { 'projectId': this.currentRealtimeProjectKey };
      this.sendEvent('leaveRealtimeRoom', data);
    }
    this.currentRealtimeProjectKey = undefined;
  }

  joinVideoRoom(projectKey: string): boolean {
    let success = false;
    this.leaveVideoRoom();
    this.currentVideoProjectKey = projectKey;

    if (this.videoProjectValid()) {
      const data = { 'projectId': projectKey };
      this.sendEvent('joinVideoRoom', data);
      success = true;
    }
    return success;
  }

  leaveVideoRoom(): void {
    if (this.videoProjectValid()) {
      const data = { 'projectId': this.currentVideoProjectKey };
      this.sendEvent('leaveVideoRoom', data);
    }
    this.currentVideoProjectKey = undefined;
  }

  joinChatRoom(projectKey: string): boolean {
    let success = false;
    this.leaveChatRoom();
    this.currentChatProjectKey = projectKey;

    if(this.chatProjectValid()){
      const data = {'projectId': this.currentChatProjectKey};
      this.sendEvent('joinChatRoom', data);
      success = true;
    }
    return success;
  }

  leaveChatRoom(): void {
    if (this.chatProjectValid()) {
      const data = { 'projectId': this.currentChatProjectKey };
      this.sendEvent('leaveChatRoom', data);
    }
    this.currentChatProjectKey = undefined;
  }

  joinCopilotRoom(projectKey: string): boolean {
    let success = false;
    this.currentCopilotProjectKey = projectKey;

    if (this.copilotProjectValid()) {
      const data = { 'projectId': projectKey };
      this.sendEvent('joinCopilotRoom', data); // TODO use enum for event type
      success = true;
    }
    console.log('joinCopilotRoom:', success);
    return success;
  }

  leaveCopilotRoom(): void {
    if (this.copilotProjectValid()) {
      const data = { 'projectId': this.currentCopilotProjectKey, 'meetingId': this.currentCopilotMeetingId };
      this.sendEvent('leaveCopilotRoom', data); // TODO use enum for event type
    }
    this.currentVideoProjectKey = undefined;
  }

  getSocketId(): string {
    const socketId = this.socket.ioSocket['id'];
    if (!socketId) {
      this.notifier.notify('error', 'No valid socket-ID.');
    }
    return socketId;
  }

  isConnected(): boolean {
    return this.socket.ioSocket['connected'];
  }

  isDisconnected(): boolean {
    return this.socket.ioSocket['disconnected'];
  }

  public realtimeProjectValid(): boolean {
    return this.isValidKeyString(this.currentRealtimeProjectKey);
  }
  private persistenceProjectValid(): boolean {
    return this.isValidKeyString(this.currentPersistenceProjectKey);
  }

  private videoProjectValid(): boolean {
    return this.isValidKeyString(this.currentVideoProjectKey);
  }

  private chatProjectValid(): boolean {
    return this.isValidKeyString(this.currentChatProjectKey);
  }


  private copilotProjectValid(): boolean {
    return this.isValidKeyString(this.currentCopilotProjectKey);
  }

  private isValidKeyString(key: string) {
    return (key !== undefined && key !== null && key !== '');
  }

  public isInVideoCall(): boolean {
    return this.userCount > 1;
  }
}
